import {
  APIFieldAttributes,
  APIWorkspaceAttributes,
  ArdoqId,
} from '@ardoq/api-types';
import { isEqual } from '@ardoq/common-helpers';
import { spreadMap } from '@ardoq/rxbeach';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, shareReplay } from 'rxjs/operators';
import { fields$ } from 'streams/fields/fields$';
import { StreamCollection } from 'streams/utils/streamUtils';
import { persistentWorkspaces$ as workspaces$ } from 'streams/workspaces/workspaces$';

export type FieldsToWorkspacesMap = Record<string, Set<ArdoqId>>;

export const buildFieldsToWorkspacesMap = (
  fieldsState: StreamCollection<APIFieldAttributes>,
  workspacesState: StreamCollection<APIWorkspaceAttributes>
): FieldsToWorkspacesMap => {
  const modelIdToWorkspaces = new Map<ArdoqId, Set<ArdoqId>>();

  workspacesState.models.forEach(workspace => {
    const workspaceModelId = workspace.componentModel;
    if (!modelIdToWorkspaces.has(workspaceModelId)) {
      modelIdToWorkspaces.set(workspaceModelId, new Set());
    }
    modelIdToWorkspaces.get(workspaceModelId)!.add(workspace._id);
  });

  const fieldsToWorkspaces: FieldsToWorkspacesMap = {};

  fieldsState.models.forEach(field => {
    const fieldModelId = field.model;
    const workspacesForModel = modelIdToWorkspaces.get(fieldModelId);

    if (workspacesForModel && workspacesForModel.size > 0) {
      if (!fieldsToWorkspaces[field.name]) {
        fieldsToWorkspaces[field.name] = new Set();
      }
      workspacesForModel.forEach(workspaceId => {
        fieldsToWorkspaces[field.name].add(workspaceId);
      });
    }
  });

  return fieldsToWorkspaces;
};

export const fieldsToWorkspaces$ = combineLatest([fields$, workspaces$]).pipe(
  spreadMap(buildFieldsToWorkspacesMap),
  distinctUntilChanged(isEqual),
  shareReplay({ bufferSize: 1, refCount: true })
);
